import { useCallback, useEffect, useMemo, useState } from 'react';

type RecommendPicture = { pictureUrl: string; userNickname: string };

interface UseBettieRecommendedProfilePicturesProps {
  recommendPictures?: RecommendPicture[];
  existingPictureUrls: string[];
  onAppendRecommendedPictureUrl: (pictureUrl: string, userNickname: string) => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onFetchNextPage?: () => void;
  onFailedRecommend?: () => void;
}

export const useBettieRecommendedProfilePictures = ({
  recommendPictures,
  existingPictureUrls,
  onAppendRecommendedPictureUrl,
  hasNextPage,
  isFetchingNextPage,
  onFetchNextPage,
  onFailedRecommend
}: UseBettieRecommendedProfilePicturesProps) => {
  const [usedRecommendPictureUrls, setUsedRecommendPictureUrls] = useState<string[]>([]);
  const [previewRecommendPictureUrl, setPreviewRecommendPictureUrl] = useState<string | null>(null);

  const candidates = useMemo(() => {
    const usedSet = new Set(usedRecommendPictureUrls);
    const existingSet = new Set(existingPictureUrls);
    return (recommendPictures ?? []).filter(
      (p) => !usedSet.has(p.pictureUrl) && !existingSet.has(p.pictureUrl)
    );
  }, [existingPictureUrls, recommendPictures, usedRecommendPictureUrls]);

  // "추천받기"로 실제로 나올 수 있는 후보들
  const recommendPool = useMemo(() => {
    if (!previewRecommendPictureUrl) return candidates;
    return candidates.filter((c) => c.pictureUrl !== previewRecommendPictureUrl);
  }, [candidates, previewRecommendPictureUrl]);

  const remainingCount = recommendPool.length;

  // "새 프사 추천받기" 버튼에 보여줄 미리보기 1장
  useEffect(() => {
    // 미리보기는 "고정"이어야 하므로, 한 번 정해지면 페이지네이션으로 후보가 늘어나도 바꾸지 않음.
    const isCurrentPreviewValid = previewRecommendPictureUrl
      ? candidates.some((c) => c.pictureUrl === previewRecommendPictureUrl)
      : false;

    if (previewRecommendPictureUrl && isCurrentPreviewValid) return;

    if (candidates.length < 3) {
      setPreviewRecommendPictureUrl(null);
      return;
    }

    const picked = candidates[Math.floor(Math.random() * candidates.length)];
    setPreviewRecommendPictureUrl(picked?.pictureUrl ?? null);
  }, [candidates, previewRecommendPictureUrl]);

  const handleBeforeRecommendNewProfile = useCallback(() => {
    const hasRemaining = recommendPool.length > 0;
    if (!hasRemaining) {
      if (hasNextPage) {
        if (!isFetchingNextPage) onFetchNextPage?.();
        return false;
      }

      onFailedRecommend?.();
      return false;
    }
    return true;
  }, [hasNextPage, isFetchingNextPage, onFetchNextPage, recommendPool.length, onFailedRecommend]);

  const handleAfterRecommendNewProfile = useCallback(() => {
    if (recommendPool.length === 0) {
      if (hasNextPage) {
        if (!isFetchingNextPage) onFetchNextPage?.();
        return;
      }

      onFailedRecommend?.();
      return;
    }

    const picked = recommendPool[Math.floor(Math.random() * recommendPool.length)];
    if (!picked) return;

    const pickedUrl = picked.pictureUrl;
    const pickedUserNickname = picked.userNickname;
    setUsedRecommendPictureUrls((prev) => (prev.includes(pickedUrl) ? prev : [...prev, pickedUrl]));
    onAppendRecommendedPictureUrl(pickedUrl, pickedUserNickname);
  }, [
    hasNextPage,
    isFetchingNextPage,
    onAppendRecommendedPictureUrl,
    onFetchNextPage,
    recommendPool,
    onFailedRecommend
  ]);

  return {
    handleBeforeRecommendNewProfile,
    handleAfterRecommendNewProfile,
    usedRecommendPictureUrls,
    previewRecommendPictureUrl,
    remainingCount
  };
};
